import { MenuItem, Recipe, Size } from '@/models/menuModels';
import ApiHelper from '@/services/ApiHelper';
import { LineItem } from '@/models/cartModels';
import { mapMenuItemToLineItem } from '@/services/orderService';
import { getLocalStorage, localStorageEnum, replaceLocalStorage } from '@/services/localStorageService';
import { ActiveBundleDetails, Bundle, BundleDTO, BundleGroup } from '@/models/bundleModels';
import { isBundleGroupFull, mapBundleGroupItemsToMenuItems } from '@/services/bundleService';
import { ActionContext } from 'vuex';
import { StoreLocation } from '@/models/locationModels';
import { createSlug } from '@/services/stringService';

export interface BundleState {
    activeBundle: Bundle | null;
    activeGroupIndex: number | null;
    activeGroup: BundleGroup | null;
    activeGroupItems: ReadonlyArray<MenuItem> | null;
    bundles: ReadonlyArray<Bundle>;
    bundlesLoaded: boolean;
    cart: ReadonlyArray<LineItem[]>;
    identifier: string;
    editingIdentifier: string | null;
    activeCouponNumber: string | null;
    valueBundle: Bundle | null;
}

const localBundleState = getLocalStorage(localStorageEnum.bundleState);

if (localBundleState) {
    // remove bundles from state
    localBundleState.bundles = [];
    localBundleState.bundlesLoaded = false;
    // Persisted cart items come back as plain objects; re-wrap them so LineItem getters/methods survive rehydration
    localBundleState.cart = (localBundleState.cart ?? []).map((group: LineItem[]) => (group ?? []).map((item: LineItem) => new LineItem(item)));
}

const BundleState: BundleState = localBundleState ?? {
    activeCouponNumber: null,
    activeGroupIndex: null,
    activeGroup: null,
    activeGroupItems: null,
    activeBundle: null,
    valueBundle: null,
    bundles: [],
    bundlesLoaded: false,
    cart: [],
    identifier: '',
    editingIdentifier: null,
};

export default {
    state: BundleState,
    mutations: {
        SET_BUNDLES(state: BundleState, bundles: Bundle[]) {
            state.bundles = bundles;
            state.bundlesLoaded = true;
        },
        SET_ACTIVE_BUNDLE(state: BundleState, bundle: Bundle | null) {
            state.activeBundle = bundle;
        },
        ADD_LINE_ITEM_TO_CART(state: BundleState, lineItem: LineItem) {
            if (state.activeGroupIndex === null) {
                return;
            }
            const groupIndex = state.activeGroupIndex;
            const activeGroup = state.activeGroup;
            const updatedCart = [...state.cart];
            const groupItems = [...(updatedCart[groupIndex] ?? [])];

            // A group page reached directly bypasses the change-item prompt, so re-picking a full group replaces
            // rather than stacks — an over-filled group would reach the cart at deal pricing.
            if (activeGroup && isBundleGroupFull(groupItems.length, activeGroup)) {
                groupItems.splice(activeGroup.maxAllowed - 1);
            }

            groupItems.push(lineItem);
            updatedCart[groupIndex] = groupItems;
            state.cart = updatedCart;
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        CLEAR_BUNDLE_CART(state: BundleState) {
            state.cart = [];
            state.editingIdentifier = null;
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        SET_EDITING_IDENTIFIER(state: BundleState, identifier: string | null) {
            state.editingIdentifier = identifier;
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        SET_VALUE_BUNDLE(state: BundleState, bundle: Bundle | null) {
            state.valueBundle = bundle;
        },
        SET_BUNDLE_CART(state: BundleState, cart: any) {
            state.cart = cart;
        },
        REMOVE_ITEM_FROM_GROUP(state: BundleState, data: any) {
            state.cart[data.groupIndex].splice(data.itemIndex, 1);
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        SET_ACTIVE_COUPON_NUMBER(state: BundleState, couponNumber: any) {
            // Normalize to a string so the cart-clear guard compares like types regardless of
            // whether the caller passes a parsed number (bundlePage) or the raw route param string.
            const normalizedCouponNumber = couponNumber != null ? String(couponNumber) : null;
            if (normalizedCouponNumber !== state.activeCouponNumber) {
                state.cart = [];
                state.editingIdentifier = null;
            }
            state.activeCouponNumber = normalizedCouponNumber;
            state.identifier = normalizedCouponNumber + '-' + Date.now();
            replaceLocalStorage(localStorageEnum.bundleState, state);
        },
        SET_ACTIVE_GROUP_INDEX(state: BundleState, idx: any) {
            state.activeGroupIndex = idx;
        },
        SET_ACTIVE_GROUP(state: BundleState, group: BundleGroup | null) {
            state.activeGroup = group;
        },
        SET_ACTIVE_GROUP_ITEMS(state: BundleState, items: ReadonlyArray<MenuItem> | null) {
            state.activeGroupItems = items;
        },
    },
    actions: {
        initActiveBundle(context: ActionContext<BundleState, any>, payload: ActiveBundleDetails) {
            context.commit('SET_ACTIVE_COUPON_NUMBER', payload.couponNumber);
            context.commit('SET_ACTIVE_GROUP_INDEX', payload.groupIndex);

            const activeBundle: Bundle | null = context.state.bundles.find((b) => parseInt(b.couponNumber) === parseInt(payload.couponNumber)) ?? null;
            context.commit('SET_ACTIVE_BUNDLE', activeBundle ?? null);

            // bundleGroups are sorted in setBundles; group items are sorted in mapBundleGroupItemsToMenuItems
            const activeGroup: BundleGroup | null = activeBundle ? activeBundle.bundleGroups[payload.groupIndex] ?? null : null;
            context.commit('SET_ACTIVE_GROUP', activeGroup);
            context.commit('SET_ACTIVE_GROUP_ITEMS', activeGroup ? activeGroup.items : null);
        },
        async initBundleItem(context: ActionContext<BundleState, any>, payload: ActiveBundleDetails): Promise<void> {
            try {
                context.dispatch('initActiveBundle', payload);

                const activeGroup: BundleGroup | null = context.state.activeGroup;
                const menuItem: MenuItem | null = activeGroup ? activeGroup.items.find((i) => i.itemId === payload.itemId) ?? null : null;
                context.commit('SET_MENU_ITEM', menuItem);

                if (!menuItem || !menuItem?.recipes) {
                    return;
                }
                const menuCategory = context.getters.getLocationMenu?.find((c) => createSlug(c.name) === menuItem.categoryName.toLowerCase());
                context.commit('SET_MENU_CATEGORY', menuCategory ?? null);
                const recipes: ReadonlyArray<Recipe> = menuItem?.recipes;
                context.commit('SET_SELECTED_CRUST', recipes[0]);
                const size: Size | null = recipes ? recipes[0].sizes[0] : null;
                context.commit('SET_SELECTED_SIZE', size);

                if (!size) return;

                const availableTopping = await context.dispatch('setAvailableTopping', { menuItem, recipe: recipes[0], size });
                context.commit('SET_AVAILABLE_TOPPING', availableTopping);

                const lineItem = mapMenuItemToLineItem(menuItem, size, recipes[0], availableTopping);
                context.commit('SET_LINE_ITEM', lineItem);

                context.dispatch('setLineItemDefaults');

                await context.dispatch('updateLineItemPriceAndCal');

                context.dispatch('validateRequiredSelections');
            } catch (error) {
                throw new Error('Failed to initialize value item.');
            }
            return;
        },
        removeItemFromGroup(context: ActionContext<BundleState, any>, data: any) {
            context.commit('REMOVE_ITEM_FROM_GROUP', data);
        },
        resetBundle(context: ActionContext<BundleState, any>) {
            context.commit('CLEAR_BUNDLE_CART');
        },
        addToBundle(context: ActionContext<BundleState, any>) {
            context.commit('ADD_LINE_ITEM_TO_CART', context.getters.getCurrentLineItem);
        },
        // Which bundle, if any, this build replaces. Resolved before anything is added, because the new
        // items share the coupon prefix the value-bundle lookup matches on.
        resolveReplacedIdentifier(context: any, bundle: Bundle): string | null {
            if (context.state.editingIdentifier) {
                return context.state.editingIdentifier;
            }
            if (!bundle.isValue) {
                return null;
            }
            // A direct builder link (e.g. a CMS banner) bypasses the /pizza-pals guard, landing a user who
            // already owns the bundle in a fresh build. Replace it rather than duplicating it.
            const existing = context.rootState.orderModule.cartItems.find((item: LineItem) => item.bundleIdentifier?.startsWith(bundle.couponNumber));
            return existing ? existing.bundleIdentifier.split('--')[0] : null;
        },
        // Commits the staged build to the order cart, replacing the bundle it edits. Resolves true once
        // the cart holds the whole deal; false if it was rolled back, leaving the builder open to retry.
        async confirmBundle(context: any, bundle: Bundle): Promise<boolean> {
            const rewardsBasketItems = context.rootGetters.getRewardsBasketInfo?.basketItems;
            if (rewardsBasketItems?.length > 0) {
                context.dispatch('notify', { message: 'Your reward was removed. Deals cannot be combined with rewards at this time.', type: 'info' }, { root: true });

                // Awaited so the basket is empty before the coupon below reaches the server
                await Promise.all(rewardsBasketItems.map((reward: any) => context.dispatch('removeRewardFromBasket', { rewardId: reward.discountId, validate: false }, { root: true })));
            }

            context.dispatch('setSkipPriceAlignment', true, { root: true });
            try {
                const identifierToReplace = await context.dispatch('resolveReplacedIdentifier', bundle);
                const bundleIdentifier = context.state.identifier;

                // Add before removing, so a failed add leaves the existing bundle and coupon untouched
                // rather than deleting a bundle for a build that never landed.
                let addFailed = false;
                for (const [groupIndex, groupItems] of context.state.cart.entries()) {
                    for (const item of groupItems ?? []) {
                        // While editing these are the objects still in the order cart, and the cart mutations
                        // match by reference — adding the original would fuse the old and new line items.
                        const itemToAdd = new LineItem(item);
                        itemToAdd.bundleIdentifier = `${bundleIdentifier}--${groupIndex}`;
                        try {
                            // addCartItem identifies the new line from the end of the returned basket, so these
                            // requests must be serialized to keep each response paired with the item it added.
                            await context.dispatch('addCartItem', itemToAdd, { root: true });
                        } catch {
                            addFailed = true;
                            break;
                        }
                    }
                    if (addFailed) break;
                }

                if (addFailed) {
                    await context.dispatch('rollbackPartialBundle', bundleIdentifier);
                    return false;
                }

                // Removal drops the replaced bundle's coupon, so it has to precede addCoupon.
                if (identifierToReplace) {
                    await context.dispatch('removeBundleFromCart', identifierToReplace);
                }
                context.dispatch('addCoupon', { code: bundle.couponNumber.padStart(3, '0') }, { root: true });
                return true;
            } finally {
                context.dispatch('setSkipPriceAlignment', false, { root: true });
                context.dispatch('alignPrice', null, { root: true });
            }
        },
        async rollbackPartialBundle(context: any, bundleIdentifier: string) {
            const partialItems = context.rootState.orderModule.cartItems.filter((item: LineItem) => item.bundleIdentifier?.startsWith(bundleIdentifier));
            await Promise.allSettled(partialItems.map((item: LineItem) => context.dispatch('deleteCartItem', item, { root: true })));

            // Anything the delete could not remove is detached rather than left carrying the identifier:
            // the cart getters hide bundle items from the main list, so a survivor would otherwise render
            // as a phantom one-item deal at full price.
            context.commit('DETACH_BUNDLE_ITEMS', bundleIdentifier, { root: true });

            // Fresh identifier so the retry can't reuse one an orphan may still hold. Re-committing the
            // same coupon number regenerates it without clearing the builder.
            context.commit('SET_ACTIVE_COUPON_NUMBER', context.state.activeCouponNumber);
        },
        async fetchBundles(context: ActionContext<BundleState, any>) {
            const location: StoreLocation = context.getters.getSelectedLocation;
            if (!location) {
                return;
            }

            try {
                const res = await ApiHelper.BlackBrick.get(`/stores/${location.id}/coupons/bundles`);
                context.dispatch('setBundles', res.data);
            } catch (error) {
                console.error('Failed to fetch bundles', error);
            }
            return;
        },
        setBundles(context: ActionContext<BundleState, any>, bundlesDTO: BundleDTO[]) {
            const bundles: Bundle[] = bundlesDTO.map((bundle) => {
                return {
                    name: bundle.name,
                    subText: bundle.sub_text,
                    badgeText: bundle.badge_text,
                    imageUrl: bundle.image_url,
                    couponNumber: String(bundle.coupon_number),
                    isValue: bundle.is_value,
                    valuePrice: bundle.value_price,
                    bundleGroups: bundle.bundle_groups
                        .map((bg) => {
                            const bundleGroup: BundleGroup = {
                                name: bg.name,
                                sort: bg.sort,
                                minRequired: bg.min_required,
                                maxAllowed: bg.max_allowed,
                                displayStyle: bg.display_style,
                                items: mapBundleGroupItemsToMenuItems(context.getters.getLocationMenu, bg.items),
                            };
                            return bundleGroup;
                        })
                        .sort((a, b) => a.sort - b.sort),
                };
            });
            context.commit('SET_BUNDLES', bundles);

            context.commit(
                'SET_VALUE_BUNDLE',
                bundles.find((b) => b.isValue)
            );
        },
        async removeBundleFromCart(context: any, identifier: string) {
            const bundleItems = context.rootState.orderModule.cartItems.filter((item: any) => item.bundleIdentifier?.startsWith(identifier));
            const couponNumber = identifier.substring(0, 3);

            const coupon = context.rootState.orderModule.validCoupons.find((c: any) => parseInt(c.code) === parseInt(couponNumber));

            // batching to have a single align across the entire add-then-remove sequence.
            const callerBatching = context.getters.getSkipPriceAlignment;
            if (!callerBatching) {
                context.dispatch('setSkipPriceAlignment', true, { root: true });
            }

            try {
                if (coupon) {
                    context.dispatch('removeCoupon', coupon, { root: true });
                }
                // Settled, not all: a failed delete already toasted, and callers here are fire-and-forget.
                await Promise.allSettled(bundleItems.map((item: any) => context.dispatch('deleteCartItem', item, { root: true })));
            } finally {
                if (!callerBatching) {
                    context.dispatch('setSkipPriceAlignment', false, { root: true });
                    context.dispatch('alignPrice', null, { root: true });
                }
            }
        },
        convertBundleToCartItems(context: any, couponNumber: string) {
            const bundleItems = context.rootState.orderModule.cartItems.filter((item: any) => {
                return item.bundleIdentifier && item.bundleIdentifier.startsWith(couponNumber);
            });

            bundleItems.forEach((item: any) => {
                item.bundleIdentifier = null;
            });
        },
        changeCartBundle(context: any, identifier: string) {
            const bundleItems = context.rootState.orderModule.cartItems.filter((item: any) => {
                return item.bundleIdentifier && item.bundleIdentifier.startsWith(identifier);
            });

            const couponNumber = identifier.substring(0, 3);

            // Stage the existing bundle items for editing without touching the order cart.
            const newBundleCart: any[] = [];
            bundleItems.forEach((item: any) => {
                const groupIndex = item.bundleIdentifier.split('--')[1];
                if (!Array.isArray(newBundleCart[groupIndex])) {
                    newBundleCart[groupIndex] = [];
                }
                newBundleCart[groupIndex].push(item);
            });

            context.commit('SET_ACTIVE_COUPON_NUMBER', couponNumber);
            context.commit('SET_BUNDLE_CART', newBundleCart);
            context.commit('SET_EDITING_IDENTIFIER', identifier);
        },
        setActiveCouponNumber(context: any, couponNumber: string) {
            context.commit('SET_ACTIVE_COUPON_NUMBER', couponNumber);
        },
    },
    getters: {
        getNonValueBundles(state: BundleState) {
            return state.bundles.filter((b) => !b.isValue);
        },
        getValueBundle(state: BundleState) {
            return state.valueBundle;
        },
        getAllBundles(state: BundleState) {
            return state.bundles;
        },
        getBundleIdentifier(state: BundleState) {
            return state.identifier;
        },
        getEditingIdentifier(state: BundleState) {
            return state.editingIdentifier;
        },
        getActiveBundle(state: BundleState) {
            return state.activeBundle;
        },
        getActiveGroup(state: BundleState) {
            return state.activeGroup;
        },
        getActiveGroupItems(state: BundleState) {
            return state.activeGroupItems;
        },
        getActiveCouponNumber(state: BundleState) {
            return state.activeCouponNumber;
        },
        getActiveGroupIndex(state: BundleState) {
            return state.activeGroupIndex;
        },
        bundlesLoaded(state: BundleState) {
            return state.bundlesLoaded;
        },
        bundleCart(state: BundleState) {
            return state.cart;
        },
        // True as soon as anything is picked, whereas activeBundle only lands once a group page runs
        // initActiveBundle. Routing needs this; bundleInProgress keeps activeBundle for its renderers.
        hasStagedBundleItems(state: BundleState) {
            return state.cart.some((group) => group?.length > 0);
        },
        // A staged build belongs to the coupon it was started under, so callers have to name that coupon.
        hasStagedBundleFor: (state: BundleState, getters: any) => (couponNumber: string) => parseInt(state.activeCouponNumber ?? '') === parseInt(couponNumber) && getters.hasStagedBundleItems,
        bundleInProgress(state: BundleState, getters: any) {
            return !!state.activeBundle && getters.hasStagedBundleItems;
        },
        // A staged edit is a copy of a bundle already complete in the cart, so it is never an unfinished
        // deal to prompt about — only a build the user has not committed yet is.
        hasUnfinishedBuild(state: BundleState, getters: any) {
            return getters.bundleInProgress && !state.editingIdentifier;
        },
    },
};
